/**
 * ShareWithFamilyAction — button + modal flow on the personal item detail
 * page that lets any family member share a personal item into a family safe.
 *
 * Visibility rules:
 *   - Hidden when the user is in 0 families (nothing to share to).
 *   - Hidden when the item is already shared (`item.orgId` set) — sharing the
 *     same item again would duplicate it.
 *   - Visible for both admin AND non-admin members. Any member may create
 *     (and later edit) the shared item; only an admin may remove it.
 *
 * Semantics — sharing MOVES the item: the ECIES-wrapped family copy is pushed,
 * and only once the server has accepted it (not in `rejectedItemIds`) is the
 * personal original deleted; the page then navigates to the family copy. A
 * copy-and-keep model left the personal original offering "Share with family"
 * again (duplicates) and let the two drift apart.
 *
 * Click flow:
 *   - Single-family member → confirm modal naming the family.
 *   - Multi-family member → picker modal with a <select>.
 *
 * Circle picker:
 *   - Admin: sees all circles (fetched via listCircles). Defaults to Everyone.
 *   - Non-admin: sees only the circles they belong to (filtered via getOrg
 *     member roster). Defaults to Everyone.
 *
 * Encryption:
 *   - Delegates the ECIES-under-family-pubkey step to `prepareShareForFamily`
 *     (sibling file). Keeping the crypto out of the component makes the
 *     component unit-testable without real crypto setup.
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSafe } from '../context/SafeContext';
import { shareItemWithFamily, listCircles, getOrg, getCurrentUserId } from '../api/client';
import { base64UrlToBytes } from '../crypto/base64url';
import { prepareShareForFamily } from './prepareShareForFamily';
import { familyKeyAuthentic } from '../pkastore/familyKeyAuth';
import type { Circle } from '@heybesafe/shared';
import type { VaultItem } from '@heybesafe/shared';

export function ShareWithFamilyAction({ item }: { item: VaultItem }) {
  const { orgs, sync, deleteItem } = useSafe();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOrgId, setSelectedOrgId] = useState<string>(orgs[0]?.id ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Once a share succeeds, we keep `lastSharedFamilyName` set so the inline
  // confirmation persists outside the modal. The modal itself closes
  // immediately on success — no auto-close timer (avoids React act() noise
  // in tests). The next click on "Share with family" clears the confirmation.
  const [lastSharedFamilyName, setLastSharedFamilyName] = useState<string | null>(null);

  // Circle picker state
  const [circles, setCircles] = useState<Circle[]>([]);
  const [selectedCircleId, setSelectedCircleId] = useState<string>('');

  // Fetch circles whenever the modal opens or the selected org changes.
  // Admins see all circles; non-admins see only circles they belong to.
  useEffect(() => {
    if (!isOpen) return;
    const orgId = selectedOrgId || orgs[0]?.id;
    if (!orgId) return;

    const targetOrg = orgs.find((o) => o.id === orgId);
    const isAdmin = targetOrg?.role === 'admin';

    let cancelled = false;

    if (isAdmin) {
      listCircles(orgId).then((r) => {
        if (!cancelled && r.ok) setCircles(r.data.circles);
      });
    } else {
      const myUserId = getCurrentUserId();
      const orgFetch = myUserId
        ? getOrg(orgId)
        : Promise.resolve(null as null);

      Promise.all([listCircles(orgId), orgFetch]).then(([circlesResult, orgResult]) => {
        if (cancelled) return;
        if (!circlesResult.ok) return;
        const allCircles = circlesResult.data.circles;
        if (orgResult && orgResult.ok) {
          const me = orgResult.data.members.find(
            (m: { userId: string; circleIds?: string[] }) => m.userId === myUserId,
          );
          const myCircleIds = new Set(me?.circleIds ?? []);
          setCircles(allCircles.filter((c) => myCircleIds.has(c.id)));
        } else {
          setCircles([]);
        }
      });
    }

    return () => {
      cancelled = true;
    };
  }, [isOpen, selectedOrgId, orgs]);

  if (orgs.length === 0) return null;
  if (item.orgId) return null;

  async function handleShare(): Promise<void> {
    const orgId = selectedOrgId || orgs[0]?.id;
    if (!orgId) return;
    const targetOrg = orgs.find((o) => o.id === orgId);
    if (!targetOrg) return;
    if (!targetOrg.publicKey) {
      setError('Family is missing its public key. Try refreshing.');
      return;
    }
    // PKA (A2): authenticate the family public key before wrapping an item to
    // it, so a malicious server can't substitute a key it controls.
    if (!familyKeyAuthentic(targetOrg)) {
      setError("Could not verify this family's security key — do not share. Ask a family admin to sign in to the latest version.");
      return;
    }

    setBusy(true);
    setError(null);
    try {
      const familyPubBytes = base64UrlToBytes(targetOrg.publicKey);
      const encrypted = await prepareShareForFamily(item, familyPubBytes, null, orgId);
      const itemToShare = selectedCircleId
        ? { ...encrypted, circleId: selectedCircleId }
        : encrypted;
      const result = await shareItemWithFamily(itemToShare);
      if (!result.ok) {
        setError('Share failed. Please try again.');
        return;
      }
      // A refused shared write still answers 200 — the id comes back in
      // rejectedItemIds. Sharing MOVES the item, so this check is what stands
      // between a refused share and deleting the only copy.
      if (result.data.rejectedItemIds?.includes(encrypted.id)) {
        setError("The family safe didn't accept this item, so it's still in your personal safe.");
        return;
      }
      // Sharing MOVES the item into the family safe: remove the personal
      // original only now that the server holds the family copy. Keeping both
      // made duplicates on a second share and let the copies drift (a password
      // change updated only the personal one).
      await deleteItem(item.id);
      // Pull the ECIES-wrapped copy into local state, then open it — the
      // personal page this action lives on no longer has an item.
      await sync();
      setLastSharedFamilyName(targetOrg.name);
      setIsOpen(false);
      navigate(`/orgs/${orgId}/items/${encrypted.id}`);
    } catch (e) {
      console.error('ShareWithFamilyAction: share failed', e);
      setError('Share failed. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  function handleOpen(): void {
    // Reset transient state every time the modal opens so a previous error
    // doesn't linger across attempts. (Success confirmation lives outside
    // the modal and clears on the next attempt.)
    setError(null);
    setLastSharedFamilyName(null);
    setSelectedOrgId(orgs[0]?.id ?? '');
    setSelectedCircleId('');
    setCircles([]);
    setIsOpen(true);
  }

  function handleCancel(): void {
    if (busy) return;
    setIsOpen(false);
    setError(null);
  }

  const isSingleFamily = orgs.length === 1;
  const selectedOrg = orgs.find((o) => o.id === (selectedOrgId || orgs[0]?.id));
  const headline = isSingleFamily
    ? `Share ${item.name} with ${orgs[0].name}?`
    : 'Pick a family to share with';

  return (
    <>
      <button
        type="button"
        id="share-item-open-button"
        onClick={handleOpen}
        className="px-3 py-1.5 text-xs text-ink-secondary hover:text-ink hover:bg-surface-raised rounded-lg transition-colors"
      >
        Share with family
      </button>

      {lastSharedFamilyName && !isOpen && (
        <span role="status" className="text-xs text-green-600 ml-2">
          Moved to {lastSharedFamilyName}. Anyone in the family can edit it there.
        </span>
      )}

      {isOpen && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
        >
          <div className="bg-surface border border-divider rounded-xl p-6 max-w-md w-full">
            <h2 className="text-base font-medium text-ink mb-4">{headline}</h2>

            {!isSingleFamily && (
              <select
                aria-label="Family"
                value={selectedOrgId}
                onChange={(e) => {
                  setSelectedOrgId(e.target.value);
                  setSelectedCircleId('');
                  setCircles([]);
                }}
                disabled={busy}
                className="w-full bg-surface-raised border border-divider rounded-lg px-3 py-2 text-sm mb-4"
              >
                {orgs.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </select>
            )}

            {!isSingleFamily && selectedOrg && (
              <p className="text-sm text-ink mb-4">
                Share <span className="font-medium">{item.name}</span> with{' '}
                <span className="font-medium">{selectedOrg.name}</span>?
              </p>
            )}

            {circles.length > 0 && (
              <div className="mb-4">
                <label
                  htmlFor="circle-picker"
                  className="block text-xs font-medium text-ink-secondary mb-1"
                >
                  Share with circle
                </label>
                <select
                  id="circle-picker"
                  aria-label="Share with circle"
                  value={selectedCircleId}
                  onChange={(e) => setSelectedCircleId(e.target.value)}
                  disabled={busy}
                  className="w-full bg-surface-raised border border-divider rounded-lg px-3 py-2 text-sm"
                >
                  <option value="">Everyone</option>
                  {circles.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <p className="text-xs text-ink-secondary mb-4">
              This moves the item into the family safe — it won't stay in your personal safe, and you'll still see it there with your family. Anyone in the family can edit it; only an admin can remove it.
            </p>

            {error && (
              <p role="alert" className="text-xs text-red-500 mb-3">
                {error}
              </p>
            )}

            <div className="flex gap-2 justify-end">
              <button
                type="button"
                id="share-item-cancel-button"
                onClick={handleCancel}
                disabled={busy}
                className="px-3 py-1.5 text-xs text-ink-secondary"
              >
                Cancel
              </button>
              <button
                type="button"
                id="share-item-confirm-button"
                onClick={handleShare}
                disabled={busy}
                className="px-3 py-1.5 text-xs bg-hbs-primary hover:bg-hbs-primary-hover text-hbs-on-primary rounded-lg font-medium"
              >
                {busy ? 'Moving…' : 'Move'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
